import React from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import { compose, graphql } from 'react-apollo';

import { withClinicalData } from 'apps/ask/contexts/clinicalData';
import { withAppContext } from 'apps/ask/contexts/app';

import { classifyMutation } from 'apps/ask/mutations/classify';

import OPTIONS from 'apps/ask/Options';

import Button from 'shared/Button';
import ImageCropper from 'shared/image_cropper/ImageCropper';
import ImageUploader from './ImageUploader';

const firstGateRejections = {
  'healthy-skin': 'We didn\'t detect anything in your photo. Please try again.',
  'tattoo': 'We do not currently support photos of skin with tattoos. Please try again.',
  'default': 'This does not look like a photo of skin. Please try again.'
};

class CropImage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: '',
      submitting: false,
    };
  }

  handleBoundingBoxChange = (boundingBox) => {
    this.boundingBox = boundingBox;
  }

  handleImageReUpload = (image) => {
    this.setState({ error: '' });
    this.props.app.onImageUpload(image);
  }

  handleNextClick = () => {
    const firstGateRejection = get(this.props.clinicalData, 'firstGateResult.error');

    if (firstGateRejection) {
      this.props.app.onViewAnyway();
    } else {
      this.props.clinicalData.setImageCrop(this.boundingBox);
      this.classifyImageFirstGate();
    }
  }

  componentDidMount() {
    const { clinicalData } = this.props;
    const image = get(clinicalData, 'image.imageDataURL');

    if (!image && this.cropImageWrapper) {
      this.cropImageWrapper.querySelector('.button--reupload').click();
    }
  }

  async classifyImageFirstGate() {
    this.setState({ submitting: true });
    const firstGateResult = await this.fetchFirstGateResults();
    this.setState({ submitting: false });

    if (!firstGateResult) return;

    this.props.app.onFirstGate(firstGateResult);
  }

  async fetchFirstGateResults() {
    try {
      const { classifyMutation, clinicalData } = this.props;

      const { data } = await classifyMutation({
        variables: {
          input: {
            model: 'first-gate',
            images: [{
              id: clinicalData.image.id,
              crop: clinicalData.image.crop
            }],
            saveScan: true
          }
        }
      });
      const result = data.snapClassify;
      const type = this.analyzeFirstGateResults(result).condition.id;
      const firstGatePassed = ['macroscopic', 'dermoscopic'].includes(type);

      return {
        type,
        error: firstGatePassed ? undefined : firstGateRejections[type] || firstGateRejections.default
      };
    } catch (error) {
      this.setState({ error: 'An unexpected error occured' });
    }
  }

  analyzeFirstGateResults(firstGateResult) {
    const threshold = OPTIONS.classify.firstGateThreshold;
    const predictions = firstGateResult.images[0].predictions;
    const healthySkin = predictions.find(p => p.condition.id === 'healthy-skin');
    const macroscopic = predictions.find(p => p.condition.id === 'macroscopic');

    const sorted = orderBy(predictions, 'probability', 'desc');
    const highestProbability = sorted[0];

    if (highestProbability === healthySkin && macroscopic.probability > threshold) {
      return macroscopic;
    }

    return highestProbability;
  }

  render() {
    const { clinicalData, minBoundingSize } = this.props;
    const { error, submitting } = this.state;
    const image = clinicalData.image;
    const uploadLabel = image && image.imageDataURL ? 'Re-upload photo' : 'Upload a photo';
    const firstGateRejection = get(clinicalData, 'firstGateResult.error');
    const errorMessage = error || firstGateRejection;
    const nextLabel = firstGateRejection ? 'Continue anyway' : 'Next';
    const anyError = !!errorMessage;

    return (
      <React.Fragment>
        <div ref={(el) => { this.cropImageWrapper = el; }} className="crop-image">
          {image && image.imageDataURL ? (
            <ImageCropper
              imageData={image.imageDataURL}
              disableBodyScroll={false}
              boundingBox={image.crop}
              minBoundingSize={minBoundingSize}
              disabled={anyError}
              showWarningSign={anyError}
              onBoundingBoxChange={this.handleBoundingBoxChange}
            />
          ) : (
            <p>No photo selected.</p>
          )}

          <ImageUploader
            className="button button--medium button--no-border button--reupload no-anim"
            label={uploadLabel}
            icon="re-upload-circle"
            onUpload={this.handleImageReUpload}
          />

          {anyError && (
            <p className="error-message">{errorMessage}</p>
          )}
        </div>

        <div className={`navigator ${anyError ? 'navigator--reverse' : 'navigator--centered'}`}>
          {firstGateRejection && (
            <ImageUploader
              className="navigator__button__next no-anim"
              label="Retake photo"
              onUpload={this.handleImageReUpload}
            />
          )}

          <Button
            className={firstGateRejection ? 'navigator__button__previous' : 'navigator__button__next'}
            onClick={this.handleNextClick}
            disabled={!image || submitting || !!error}
            showSpinner={submitting}
          >
            {submitting ? 'Processing...' : nextLabel}
          </Button>
        </div>
      </React.Fragment>
    );
  }
}

CropImage.defaultProps = {
  minBoundingSize: 300
};

CropImage.propTypes = {
  classifyMutation: PropTypes.func.isRequired,
  clinicalData: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  minBoundingSize: PropTypes.number
};

export { CropImage };

export default compose(
  graphql(classifyMutation, {
    name: 'classifyMutation'
  }),
  withClinicalData,
  withAppContext,
)(CropImage);
